import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { environment } from '@/config/environment';
import { buildSupabaseAuthHeaders } from '@/lib/auth';

export type AnnotationType =
  | 'line'
  | 'rectangle'
  | 'circle'
  | 'text'
  | 'image'
  | 'arrow'
  | 'measurement';

export interface AnnotationStyle {
  color?: string;
  strokeWidth?: number;
  opacity?: number;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textAlign?: string;
  fill?: string;
  strokeDasharray?: string;
}

export interface AnnotationData {
  // Line data
  start?: { x: number; y: number };
  end?: { x: number; y: number };

  // Rectangle data
  x?: number;
  y?: number;
  width?: number;
  height?: number;

  // Circle data
  cx?: number;
  cy?: number;
  radius?: number;

  // Text data
  text?: string;

  // Image data
  url?: string;

  // Arrow data
  label?: string;

  // Rotation (in degrees)
  rotation?: number;

  // Dimension display toggle
  showDimensions?: boolean;

  // Manual measurement value
  manualValue?: string;
}

export interface Annotation {
  id: string;
  floor_plan_id: string;
  type: AnnotationType;
  data: AnnotationData;
  style: AnnotationStyle;
  layer_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CreateAnnotationInput {
  floor_plan_id: string;
  type: AnnotationType;
  data: AnnotationData;
  style?: AnnotationStyle;
  layer_order?: number;
}

export interface UpdateAnnotationInput {
  data?: AnnotationData;
  style?: AnnotationStyle;
  layer_order?: number;
}

// Export CreateAnnotationInput as CreateAnnotationData for backward compatibility
export type CreateAnnotationData = CreateAnnotationInput;

class AnnotationService {
  async getAnnotations(floorPlanId: string): Promise<Annotation[]> {
    try {
      logger.log('Fetching annotations for floor plan:', floorPlanId);

      const authSupabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await authSupabase
        .from('floor_plan_annotations')
        .select('*')
        .eq('floor_plan_id', floorPlanId)
        .order('layer_order', { ascending: true });

      if (error) {
        logger.error('Supabase error fetching annotations:', error);
        throw error;
      }

      logger.log('Fetched annotations from database:', data);
      return data || [];
    } catch (error) {
      logger.error('Error fetching annotations:', error);
      throw error;
    }
  }

  async createAnnotation(input: CreateAnnotationInput): Promise<Annotation> {
    try {
      // VALIDATION: Prevent creating annotations with invalid coordinates
      // Note: Empty text annotations are allowed during creation (user will edit inline)
      // but they are filtered out during export to prevent html2canvas failures
      const x = input.data?.x ?? input.data?.cx ?? input.data?.start?.x;
      const y = input.data?.y ?? input.data?.cy ?? input.data?.start?.y;
      if (x !== undefined && (isNaN(x) || !isFinite(x) || Math.abs(x) > 100000)) {
        throw new Error(`Invalid x coordinate: ${x}`);
      }
      if (y !== undefined && (isNaN(y) || !isFinite(y) || Math.abs(y) > 100000)) {
        throw new Error(`Invalid y coordinate: ${y}`);
      }

      // created_by is stamped in Postgres via trigger using the Clerk JWT
      const insertData = {
        floor_plan_id: input.floor_plan_id,
        type: input.type,
        data: input.data,
        style: input.style || {},
        layer_order: input.layer_order || 0,
      };

      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      const { data, error } = await authSupabase
        .from('floor_plan_annotations')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('No data returned from INSERT');

      return data;
    } catch (error) {
      logger.error('Error creating annotation:', error);
      throw error;
    }
  }

  async updateAnnotation(id: string, input: UpdateAnnotationInput): Promise<Annotation> {
    try {
      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      // First, fetch the annotation to get its floor_plan_id (needed for RLS)
      const { data: existingAnnotation, error: fetchError } = await authSupabase
        .from('floor_plan_annotations')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!existingAnnotation) throw new Error('Annotation not found');

      const updateData: any = {
        floor_plan_id: existingAnnotation.floor_plan_id, // Required for RLS policy
        updated_at: new Date().toISOString(),
      };

      if (input.data !== undefined) updateData.data = input.data;
      if (input.style !== undefined) updateData.style = input.style;
      if (input.layer_order !== undefined) updateData.layer_order = input.layer_order;

      // Perform update WITH .select() to verify save succeeded and get actual server data
      const { data: updatedAnnotation, error } = await authSupabase
        .from('floor_plan_annotations')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Annotation update failed:', { id, error });
        throw error;
      }

      if (!updatedAnnotation) {
        throw new Error('Annotation update returned no data - save may have failed');
      }

      // Return actual server data to ensure cache is in sync
      return updatedAnnotation;
    } catch (error) {
      logger.error('Error updating annotation:', error);
      throw error;
    }
  }

  async deleteAnnotation(id: string): Promise<void> {
    try {
      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      // First, fetch the annotation to check if it's an image type
      const { data: annotation, error: fetchError } = await authSupabase
        .from('floor_plan_annotations')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!annotation) throw new Error('Annotation not found');

      // If it's an image annotation, delete the image from storage first
      if (annotation.type === 'image' && annotation.data?.url) {
        await this.deleteAnnotationImage(annotation.data.url);
      }

      // Then delete the annotation record
      const { error } = await authSupabase.from('floor_plan_annotations').delete().eq('id', id);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting annotation:', error);
      throw error;
    }
  }

  async deleteAnnotations(ids: string[]): Promise<void> {
    try {
      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      const { error } = await authSupabase.from('floor_plan_annotations').delete().in('id', ids);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting annotations:', error);
      throw error;
    }
  }

  async uploadAnnotationImage(
    file: File,
    floorPlanId: string,
    organizationId?: string,
  ): Promise<string> {
    try {
      // Use organization ID if provided, otherwise use floor plan ID for path
      // We can't use Clerk user ID as it's not a UUID
      const pathPrefix = organizationId || floorPlanId;

      const fileExt = file.name.split('.').pop();
      const safeExt = fileExt ? fileExt.toLowerCase() : 'png';
      const fileName = `${pathPrefix}/${floorPlanId}/${Date.now()}.${safeExt}`;

      const SUPABASE_URL = environment.supabase.url;
      if (!SUPABASE_URL) {
        throw new Error('Missing Supabase configuration');
      }

      // Prefer using a Clerk JWT even in local skip-auth so storage policies pass
      const headers = await buildSupabaseAuthHeaders(undefined, { preferJwt: true });

      const uploadHeaders: Record<string, string> = {
        ...headers,
        'Content-Type': file.type || 'application/octet-stream',
        'x-upsert': 'false',
      };

      const response = await fetch(
        `${SUPABASE_URL}/storage/v1/object/annotation-images/${fileName}`,
        {
          method: 'POST',
          headers: uploadHeaders,
          body: file,
        },
      );

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        logger.error('Annotation image upload failed', {
          status: response.status,
          error: errorPayload,
        });
        throw new Error(errorPayload?.message || 'Failed to upload annotation image');
      }

      const result = await response.json();
      const rawKey: string = typeof result?.Key === 'string' ? result.Key : fileName;
      const storagePath = rawKey.startsWith('annotation-images/')
        ? rawKey.slice('annotation-images/'.length)
        : rawKey;

      // Return public URL (bucket is public)
      const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/annotation-images/${storagePath}`;
      return publicUrl;
    } catch (error) {
      logger.error('Error uploading annotation image:', error);
      if (error instanceof Error && error.message.includes('authentication')) {
        throw new Error(
          'Authentication required to upload annotation images. Please sign in and try again.',
        );
      }
      throw error;
    }
  }

  async deleteAnnotationImage(url: string): Promise<void> {
    try {
      // Extract the path from the URL
      const urlParts = url.split('/annotation-images/');
      if (urlParts.length < 2) {
        throw new Error('Invalid annotation image URL');
      }
      const path = urlParts[1];

      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      const { error } = await authSupabase.storage.from('annotation-images').remove([path]);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting annotation image:', error);
      // Don't throw here as the image might already be deleted
      logger.warn('Failed to delete annotation image:', error);
    }
  }

  async updateLayerOrder(annotations: { id: string; layer_order: number }[]): Promise<void> {
    try {
      // Use authenticated Supabase client for Clerk integration
      const authSupabase = await getAuthenticatedSupabaseClient();

      // First, fetch all annotations to get their floor_plan_ids (needed for RLS)
      const ids = annotations.map(a => a.id);
      const { data: existingAnnotations, error: fetchError } = await authSupabase
        .from('floor_plan_annotations')
        .select('id, floor_plan_id')
        .in('id', ids);

      if (fetchError) throw fetchError;
      if (!existingAnnotations || existingAnnotations.length === 0) {
        throw new Error('No annotations found');
      }

      // Create a map of id -> floor_plan_id for quick lookup
      const floorPlanIdMap = new Map(existingAnnotations.map(a => [a.id, a.floor_plan_id]));

      // Update each annotation's layer order with floor_plan_id included
      const promises = annotations.map(({ id, layer_order }) => {
        const floor_plan_id = floorPlanIdMap.get(id);
        if (!floor_plan_id) {
          throw new Error(`Floor plan ID not found for annotation ${id}`);
        }

        return authSupabase
          .from('floor_plan_annotations')
          .update({
            floor_plan_id, // Required for RLS policy
            layer_order,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);
      });

      const results = await Promise.all(promises);

      // Check for errors
      results.forEach((result, index) => {
        if (result.error) {
          throw new Error(
            `Failed to update annotation ${annotations[index].id}: ${result.error.message}`,
          );
        }
      });
    } catch (error) {
      logger.error('Error updating layer order:', error);
      throw error;
    }
  }
}

export const annotationService = new AnnotationService();
